import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { mapForm } from '@v2/components/pages/gift/hooks';
import { useIsFormExists } from '@v2/store/rtk.form.selectors';

import dayjs from 'dayjs';
import { useIsLogged, useUser } from '../store/rtk.user.selectors';
import { initForm, updateFormPartial } from '../store/rtk.form.store';

export function useInitForm(formId: string, formData: Object, delay?: number) {
    const dispatch = useDispatch();

    useEffect(() => {
        let timeout: any;
        if(delay) {
            timeout = setTimeout(() => {
                dispatch(initForm({ formId: formId, formData: formData }));
            }, delay);
        } else {
            dispatch(initForm({ formId: formId, formData: formData }));
        }

        return () => {
            clearTimeout(timeout);
        }
    }, []);

    return formData;
}

export function useInitFormGift(formId: string, formData: Object) {
    const dispatch = useDispatch();
    const isLogged = useIsLogged();
    const user = useUser();
    const isFormExists = useIsFormExists(formId);

    useEffect(() => {
        if(!isFormExists) {
            dispatch(initForm({
                formId: formId,
                formData: (isLogged && user)
                                ? mapForm(formData, user)
                                : formData,
            }));
        }
    }, [isLogged, isFormExists]);

    return formData;
}

export function useInitCartForm(formId: string, formData: Object) {
    const dispatch = useDispatch();
    const isFormExists = useIsFormExists(formId);

    useEffect(() => {
        if(!isFormExists) {
            dispatch(initForm({
                formId: formId,
                formData,
            }));
        }
    }, [isFormExists]);

    return formData;
}


export function useFormCallbacks(formId: string) {
    const dispatch = useDispatch();

    function updateValue(data: { [key: string]: any }, callback?: (() => void)) {
        dispatch(updateFormPartial({
            formId: formId,
            data: data,
        }));

        if (callback) {
            callback();
        }
    }

    function updateGenericInput(key: string|string[], callback?: (() => void)) {
        return function (value: any) {
            updateValue({ [typeof key === 'string' ? key : key.join('.')]: value }, callback);
        }
    }

    function updateBooleanInput(key: string|string[], callback?: (() => void)) {
        return function (checked: boolean) {
            updateValue({ [typeof key === 'string' ? key : key.join('.')]: checked }, callback);
        }
    }

    function updateTextInput(key: string|string[], callback?: (() => void)) {
        return function (e: React.FormEvent<EventTarget>) {
            const value = (e.target as HTMLInputElement).value;

            updateValue({ [typeof key === 'string' ? key : key.join('.')]: value }, callback);
        }
    }

    function updateDateInput(key: string|string[], callback?: (() => void)) {
        return function (date: any) {
            updateValue({ [typeof key === 'string' ? key : key.join('.')]: dayjs(date.toString()).format('M/D/YYYY')}, callback);
        }
    }

    function updatePhoneInput(key: string|string[], callback?: (() => void)) {
        return function (value: any, country: any, e: any, formattedValue: any) {
            updateValue({ [typeof key === 'string' ? key : key.join('.')]: value }, callback);
        }
    }

    return {
        updateValue,

        updateGenericInput,
        updateBooleanInput,

        updateTextInput,
        updateDateInput,
        updatePhoneInput,
    };
}
